import Papa from 'papaparse'
import { parseBankAmount } from './amounts.ts'
import { parseStatementDate } from './dates.ts'
import { fingerprintBankRow } from './fingerprint.ts'
import type { CsvFieldError, ParsedBankRow } from './types.ts'

export const MAX_BANK_CSV_BYTES = 2 * 1024 * 1024
export const MAX_BANK_CSV_ROWS = 2_000

const REQUIRED_HEADERS = ['date', 'amount', 'otherparty'] as const

function normalizedHeader(value: string): string {
  return value.replace(/^\uFEFF/, '').trim().toLocaleLowerCase().replace(/[\s_-]+/g, '')
}

function fieldValue(
  values: readonly string[],
  headers: readonly { original: string; normalized: string }[],
  name: string,
): string {
  const index = headers.findIndex((header) => header.normalized === name)
  return index < 0 ? '' : (values[index] ?? '').trim()
}

export class BankCsvValidationError extends Error {
  constructor(readonly errors: CsvFieldError[]) {
    super('The CSV contains invalid rows')
  }
}

export async function parseBankCsv(text: string): Promise<ParsedBankRow[]> {
  if (new TextEncoder().encode(text).byteLength > MAX_BANK_CSV_BYTES) {
    throw new Error('CSV must be 2 MB or smaller')
  }

  const parsed = Papa.parse<string[]>(text, {
    delimiter: ',',
    skipEmptyLines: 'greedy',
  })
  if (parsed.errors.length > 0) {
    throw new BankCsvValidationError(
      parsed.errors.map((error) => ({
        rowNumber: (error.row ?? 0) + 1,
        field: 'CSV',
        message: error.message,
      })),
    )
  }
  if (parsed.data.length < 2) throw new Error('CSV must include a header and at least one row')

  const rawHeaders = parsed.data[0] ?? []
  const headers = rawHeaders.map((original) => ({
    original: original.replace(/^\uFEFF/, '').trim(),
    normalized: normalizedHeader(original),
  }))
  const headerErrors: CsvFieldError[] = []
  const duplicates = headers
    .filter((header, index) =>
      headers.some(
        (candidate, candidateIndex) =>
          candidateIndex < index && candidate.normalized === header.normalized,
      ),
    )
    .map((header) => header.original || '(blank)')
  if (duplicates.length > 0) {
    headerErrors.push({
      rowNumber: 1,
      field: 'headers',
      message: `Duplicate headers: ${duplicates.join(', ')}`,
    })
  }
  for (const required of REQUIRED_HEADERS) {
    if (!headers.some((header) => header.normalized === required)) {
      headerErrors.push({
        rowNumber: 1,
        field: required === 'otherparty' ? 'other-party' : required,
        message: 'Required header is missing',
      })
    }
  }
  if (headerErrors.length > 0) throw new BankCsvValidationError(headerErrors)

  const values = parsed.data.slice(1)
  if (values.length > MAX_BANK_CSV_ROWS) {
    throw new Error(`CSV must contain no more than ${MAX_BANK_CSV_ROWS.toLocaleString()} rows`)
  }

  const errors: CsvFieldError[] = []
  const pending: Omit<ParsedBankRow, 'sourceFingerprint'>[] = []
  values.forEach((row, index) => {
    const rowNumber = index + 2
    const dateText = fieldValue(row, headers, 'date')
    const amountText = fieldValue(row, headers, 'amount')
    const otherParty = fieldValue(row, headers, 'otherparty')
    const transactionDate = parseStatementDate(dateText)
    const amountCents = parseBankAmount(amountText)

    if (row.length > headers.length) {
      errors.push({
        rowNumber,
        field: 'CSV',
        message: `Row contains ${row.length - headers.length} unexpected field${row.length - headers.length === 1 ? '' : 's'}`,
      })
    }
    if (transactionDate == null) {
      errors.push({ rowNumber, field: 'date', message: `Invalid date “${dateText}”` })
    }
    if (amountCents == null) {
      errors.push({ rowNumber, field: 'amount', message: `Invalid amount “${amountText}”` })
    }
    if (!otherParty) {
      errors.push({ rowNumber, field: 'other-party', message: 'Other party is required' })
    }
    if (transactionDate == null || amountCents == null || !otherParty) return

    const rawData = Object.fromEntries(
      headers.map((header, headerIndex) => [
        header.original || `column ${headerIndex + 1}`,
        row[headerIndex] ?? '',
      ]),
    )
    pending.push({
      id: crypto.randomUUID(),
      rowNumber,
      transactionDate,
      amountCents,
      otherParty,
      reference: fieldValue(row, headers, 'reference'),
      particulars: fieldValue(row, headers, 'particulars'),
      analysisCode: fieldValue(row, headers, 'analysiscode'),
      description: fieldValue(row, headers, 'description'),
      rawData,
    })
  })

  if (errors.length > 0) throw new BankCsvValidationError(errors)

  return Promise.all(
    pending.map(async (row) => ({
      ...row,
      sourceFingerprint: await fingerprintBankRow(row),
    })),
  )
}
